import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, FormSuccess, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

type Phase = 'verifying' | 'ok' | 'invalid' | 'resend';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [phase, setPhase] = useState<Phase>(token ? 'verifying' : 'resend');
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await authApi.verifyEmail(token);
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: ['auth', 'me'] });
        setPhase('ok');
        setTimeout(() => nav('/app', { replace: true }), 1500);
      } catch (caught) {
        if (cancelled) return;
        setErr(readApiError(caught, t('auth.error_verify_failed', { defaultValue: 'This verification link is invalid or has expired.' }) as string));
        setPhase('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onResend(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await authApi.resendVerification(resendEmail);
      setResendSent(true);
    } catch (caught) {
      setErr(readApiError(caught, t('auth.error_generic', { defaultValue: 'Something went wrong. Please try again.' }) as string));
    } finally {
      setPending(false);
    }
  }

  let body: React.ReactNode;
  if (phase === 'verifying') {
    body = (
      <div className="flex items-center gap-3 text-[14px] text-ink-600">
        <Spinner /> {t('auth.verifying', { defaultValue: 'Verifying your email…' })}
      </div>
    );
  } else if (phase === 'ok') {
    body = <FormSuccess message={t('auth.verify_success', { defaultValue: "You're verified. Redirecting…" }) as string} />;
  } else {
    body = (
      <>
        <FormError message={err} />
        {resendSent ? (
          <FormSuccess
            message={
              t('auth.resend_success', {
                defaultValue: "If an account exists for that email, we've sent a fresh verification link.",
              }) as string
            }
          />
        ) : (
          <form onSubmit={onResend} className="space-y-4">
            <TextField
              id="email"
              type="email"
              inputMode="email"
              label={t('auth.email_label', { defaultValue: 'Email' }) as string}
              placeholder="you@company.com"
              value={resendEmail}
              onChange={setResendEmail}
              autoComplete="email"
              autoFocus
              required
            />
            <SubmitButton pending={pending}>
              {t('auth.resend_verification', { defaultValue: 'Send a new verification link' })}
            </SubmitButton>
          </form>
        )}
      </>
    );
  }

  return (
    <AuthLayout
      title={
        phase === 'ok'
          ? t('auth.verify_ok_title', { defaultValue: 'Email verified.' })
          : phase === 'verifying'
            ? t('auth.verifying_title', { defaultValue: 'One moment.' })
            : t('auth.verify_title', { defaultValue: 'Verify your email.' })
      }
      subtitle={
        phase === 'invalid' || phase === 'resend'
          ? t('auth.verify_subtitle', {
              defaultValue: 'Enter the email you signed up with to get a new verification link.',
            }) as string
          : undefined
      }
      footer={
        <span>
          <Link to="/login" className="text-accent-700 font-semibold hover:underline">
            ← {t('auth.back_to_login', { defaultValue: 'Back to sign in' })}
          </Link>
        </span>
      }
    >
      {body}
    </AuthLayout>
  );
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin text-accent-600" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  );
}
